import React, { useCallback, useEffect, useRef, useState } from "react";
import {
    Box,
    ButtonBase,
    MenuItem,
    Select,
    Stack,
    Typography,
    useTheme,
} from "@mui/material";
import { alpha, darken, lighten } from "@mui/material/styles";
import TrendingUpRounded from "@mui/icons-material/TrendingUpRounded";
import PeopleAltRounded from "@mui/icons-material/PeopleAltRounded";
import CheckCircleOutlineRounded from "@mui/icons-material/CheckCircleOutlineRounded";
import AccountBalanceWalletRounded from "@mui/icons-material/AccountBalanceWalletRounded";
import LocalOfferRounded from "@mui/icons-material/LocalOfferRounded";
import HandshakeRounded from "@mui/icons-material/HandshakeRounded";
import Brand, { PartnerStats } from "../../../api/Brand";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";
import { formatPriceCents } from "../../../utils/priceFormatter";
import BrandAvatar from "../../BrandAvatar";
import Header from "../Header";
import ColoredIcon from "../../ColoredIcon";
import BackIcon from "../../../img/Arrow_Left.svg";
import Button from "../../Button";
import ShareableLink from "../../ShareableLink";
import { FONTS, CARD_STYLES, FORM_LABEL_SX } from "../../../constants/styles";

const NOISE_TEXTURE = `url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.65' numOctaves='3' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)'/%3E%3C/svg%3E")`;

const PartnerDashboard = () => {
    const theme = useTheme();
    const primary = theme.palette.primary.main;
    const navigate = useNavigate();

    const [cookies] = useCookies(["uuid", "brand_id"]);
    const { uuid, brand_id } = cookies;

    const [mode, setMode] = useState<"total" | "month">("month");
    const [stats, setStats] = useState<PartnerStats>({
        referralAddress: "",
        discount: 0,
        total: { referred: 0, active: 0, earnings: 0 },
        month: { referred: 0, active: 0, earnings: 0 },
        brands: [],
    });

    const [isPartner, setIsPartner] = useState(false);
    const [discount, setDiscount] = useState(0);

    // Scroll tracking for compact sticky header
    const scrollRef = useRef<HTMLDivElement>(null);
    const heroRef = useRef<HTMLDivElement>(null);
    const [scrolledPastHero, setScrolledPastHero] = useState(false);

    const handleScroll = useCallback(() => {
        const el = scrollRef.current;
        const heroEl = heroRef.current;
        if (!el || !heroEl) return;
        setScrolledPastHero(el.scrollTop > heroEl.offsetHeight - 60);
    }, []);

    useEffect(() => {
        if (!uuid || !brand_id) return;

        Brand.info(uuid, brand_id)
            .then((b) => {
                if (b?.isPartner) setIsPartner(true);
            })
            .catch(console.error);

        Brand.getPartnerStats(uuid, brand_id)
            .then((data) => {
                if (data) setStats(data);
            })
            .catch(console.error);
    }, [uuid, brand_id]);

    useEffect(() => {
        setDiscount(stats.discount ?? 0);
    }, [stats.discount]);

    const handleBack = useCallback(() => {
        if (window.history.length > 1) navigate(-1);
        else navigate("/");
    }, [navigate]);

    const handleDiscountUpdate = async (e: any) => {
        const newDiscount = Number(e.target.value);
        setDiscount(newDiscount);
        Brand.updateAffiliateDiscount(uuid, brand_id, newDiscount);
    };

    const currentStats = stats[mode];
    const referralUrl = stats.referralAddress
        ? `https://jemloc.com/?ref=${stats.referralAddress}`
        : "";

    const iconBoxSx = {
        width: 40,
        height: 40,
        borderRadius: "12px",
        bgcolor: alpha(primary, 0.1),
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        flex: "0 0 auto",
        color: primary,
    };

    const statRows = [
        {
            label: "Brands referred",
            value: String(currentStats.referred),
            icon: <PeopleAltRounded sx={{ fontSize: 20 }} />,
        },
        {
            label: "Active subscriptions",
            value: String(currentStats.active),
            icon: <CheckCircleOutlineRounded sx={{ fontSize: 20 }} />,
        },
        {
            label: "Your earnings",
            value: formatPriceCents(currentStats.earnings, "EUR"),
            icon: <AccountBalanceWalletRounded sx={{ fontSize: 20 }} />,
        },
    ];

    return (
        <>
            {isPartner && <Header isPartner />}
            <Stack
                ref={scrollRef}
                onScroll={handleScroll}
                flex={1}
                sx={{ overflowY: "auto", overflowX: "hidden", bgcolor: "white" }}
            >
                {/* ── Hero ── */}
                <Stack
                    ref={heroRef}
                    alignItems="center"
                    justifyContent="center"
                    sx={{
                        width: "100%",
                        minHeight: 120,
                        position: "relative",
                        zIndex: 0,
                        background: `linear-gradient(180deg, ${lighten(primary, 0.08)} 0%, ${darken(primary, 0.08)} 100%)`,
                        transition: "background 0.3s ease",
                        pt: isPartner ? "72px" : "40px",
                        pb: 4,
                        "&::after": {
                            content: '""',
                            position: "absolute",
                            inset: 0,
                            opacity: 0.04,
                            backgroundImage: NOISE_TEXTURE,
                            backgroundRepeat: "repeat",
                            backgroundSize: "128px 128px",
                            pointerEvents: "none",
                        },
                    }}
                >
                    {!isPartner && (
                        <ButtonBase
                            onClick={handleBack}
                            sx={{
                                position: "absolute", top: 12, left: 10, zIndex: 2,
                                width: 36, height: 36, borderRadius: "50%",
                                bgcolor: "rgba(60,60,60,0.35)",
                                backdropFilter: "blur(16px)",
                                WebkitBackdropFilter: "blur(16px)",
                                border: "1px solid rgba(255,255,255,0.2)",
                                display: "flex", alignItems: "center", justifyContent: "center",
                            }}
                        >
                            <ColoredIcon icon={BackIcon} color="white" size="28px" />
                        </ButtonBase>
                    )}

                    <Box
                        sx={{
                            width: 72,
                            height: 72,
                            borderRadius: "50%",
                            bgcolor: "rgba(60,60,60,0.3)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.15)",
                            display: "flex",
                            alignItems: "center",
                            justifyContent: "center",
                            mb: 1,
                        }}
                    >
                        <HandshakeRounded sx={{ fontSize: 40, color: "white" }} />
                    </Box>

                    <Box
                        sx={{
                            bgcolor: "rgba(60,60,60,0.35)",
                            backdropFilter: "blur(16px)",
                            WebkitBackdropFilter: "blur(16px)",
                            border: "1px solid rgba(255,255,255,0.2)",
                            borderRadius: "16px",
                            px: 1.5,
                            py: 0.5,
                            maxWidth: "calc(100% - 16px)",
                            boxSizing: "border-box",
                        }}
                    >
                        <Typography
                            fontSize="12px"
                            fontWeight={500}
                            color="white"
                            textAlign="center"
                            lineHeight={1.35}
                        >
                            Affiliate Dashboard
                        </Typography>
                    </Box>
                </Stack>

                {/* ── Sticky Toolbar ── */}
                <Box
                    sx={{
                        position: "sticky",
                        top: 0,
                        zIndex: 4,
                        bgcolor: "white",
                        borderTopLeftRadius: "24px",
                        borderTopRightRadius: "24px",
                        mt: "-24px",
                        pt: 1.5,
                    }}
                >
                    <Stack
                        direction="row"
                        alignItems="center"
                        sx={{
                            maxHeight: scrolledPastHero ? 52 : 0,
                            opacity: scrolledPastHero ? 1 : 0,
                            overflow: "hidden",
                            transition: "max-height 0.25s ease, opacity 0.2s ease",
                            px: 1,
                            borderBottom: scrolledPastHero
                                ? "1px solid rgba(0,0,0,0.06)"
                                : "none",
                        }}
                    >
                        {!isPartner && (
                            <ButtonBase onClick={handleBack} sx={{ p: 0.5 }}>
                                <ColoredIcon icon={BackIcon} color={primary} size="28px" />
                            </ButtonBase>
                        )}
                        <Typography
                            fontFamily={FONTS.display}
                            fontSize="16px"
                            fontWeight={600}
                            ml={isPartner ? 1 : 0.5}
                            noWrap
                            sx={{
                                flex: 1,
                                overflow: "hidden",
                                textOverflow: "ellipsis",
                            }}
                        >
                            Affiliate Dashboard
                        </Typography>
                    </Stack>
                </Box>

                {/* ── Content ── */}
                <Stack
                    sx={{
                        position: "relative",
                        zIndex: 1,
                        bgcolor: "white",
                        boxSizing: "border-box",
                        px: 2,
                        pt: 0.5,
                        pb: "calc(4vh + 90px)",
                        "@keyframes fadeSlideIn": {
                            from: { opacity: 0, transform: "translateY(12px)" },
                            to: { opacity: 1, transform: "translateY(0)" },
                        },
                        animation: "fadeSlideIn 0.3s ease-out",
                    }}
                    spacing={2}
                >
                    {/* ── Referral Link ── */}
                    {referralUrl && (
                        <>
                            <Typography
                                fontSize="11px"
                                fontWeight={600}
                                color="text.secondary"
                                textTransform="uppercase"
                                letterSpacing="0.5px"
                                mb={-1}
                            >
                                Referral Link
                            </Typography>
                            <ShareableLink
                                url={referralUrl}
                                label="Your referral link"
                            />
                        </>
                    )}

                    {/* ── Settings ── */}
                    <Typography
                        fontSize="11px"
                        fontWeight={600}
                        color="text.secondary"
                        textTransform="uppercase"
                        letterSpacing="0.5px"
                        mb={-1}
                    >
                        Settings
                    </Typography>
                    <Stack
                        sx={CARD_STYLES}
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        spacing={1}
                    >
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            flex={1}
                            minWidth={0}
                        >
                            <Box sx={iconBoxSx}>
                                <LocalOfferRounded sx={{ fontSize: 20 }} />
                            </Box>
                            <Typography
                                sx={{
                                    ...FORM_LABEL_SX,
                                    minWidth: 0,
                                }}
                            >
                                Default discount for referred brands
                            </Typography>
                        </Stack>
                        <Select
                            value={discount}
                            onChange={handleDiscountUpdate}
                            size="small"
                            sx={{
                                width: "80px",
                                flexShrink: 0,
                                borderRadius: "10px",
                                bgcolor: alpha(primary, 0.04),
                                "& .MuiOutlinedInput-notchedOutline": {
                                    borderColor: alpha(primary, 0.12),
                                },
                                "&:hover .MuiOutlinedInput-notchedOutline": {
                                    borderColor: alpha(primary, 0.25),
                                },
                                "&.Mui-focused .MuiOutlinedInput-notchedOutline":
                                    {
                                        borderColor: primary,
                                    },
                            }}
                        >
                            {[0, 5, 10, 15, 20].map((val) => (
                                <MenuItem
                                    key={val}
                                    value={val}
                                    sx={{
                                        fontFamily: FONTS.display,
                                        fontWeight: 600,
                                    }}
                                >
                                    {val}%
                                </MenuItem>
                            ))}
                        </Select>
                    </Stack>

                    {/* ── Performance Summary ── */}
                    <Stack
                        direction="row"
                        justifyContent="space-between"
                        alignItems="center"
                        mb={-1}
                    >
                        <Typography
                            fontSize="11px"
                            fontWeight={600}
                            color="text.secondary"
                            textTransform="uppercase"
                            letterSpacing="0.5px"
                        >
                            Performance
                        </Typography>
                        <Stack direction="row" spacing={0.5}>
                            <Button
                                variant="small"
                                color={
                                    mode === "month"
                                        ? "primary"
                                        : "transparent"
                                }
                                onClick={() => setMode("month")}
                                sx={{ minWidth: "min-content" }}
                            >
                                This Month
                            </Button>
                            <Button
                                variant="small"
                                color={
                                    mode === "total"
                                        ? "primary"
                                        : "transparent"
                                }
                                onClick={() => setMode("total")}
                                sx={{ minWidth: "min-content" }}
                            >
                                Total
                            </Button>
                        </Stack>
                    </Stack>

                    <Stack sx={CARD_STYLES} spacing={2}>
                        <Stack
                            direction="row"
                            alignItems="center"
                            spacing={1}
                            mb={0.5}
                        >
                            <Box sx={iconBoxSx}>
                                <TrendingUpRounded sx={{ fontSize: 20 }} />
                            </Box>
                            <Typography
                                fontFamily={FONTS.display}
                                fontSize="16px"
                                fontWeight={600}
                            >
                                Performance summary
                            </Typography>
                        </Stack>

                        {statRows.map((row) => (
                            <Stack
                                key={row.label}
                                direction="row"
                                justifyContent="space-between"
                                alignItems="center"
                            >
                                <Typography
                                    fontSize="14px"
                                    color="text.secondary"
                                >
                                    {row.label}
                                </Typography>
                                <Typography
                                    fontFamily={FONTS.display}
                                    fontSize="16px"
                                    fontWeight={700}
                                    flexShrink={0}
                                    ml={1}
                                >
                                    {row.value}
                                </Typography>
                            </Stack>
                        ))}
                    </Stack>

                    {/* ── Referred Brands ── */}
                    {stats.brands.length > 0 && (
                        <>
                            <Typography
                                fontSize="11px"
                                fontWeight={600}
                                color="text.secondary"
                                textTransform="uppercase"
                                letterSpacing="0.5px"
                                mb={-1}
                            >
                                Referred Brands
                            </Typography>
                            <Stack spacing={1}>
                                {stats.brands.map((brand) => (
                                    <Stack
                                        key={brand.id}
                                        sx={CARD_STYLES}
                                        direction="row"
                                        alignItems="center"
                                        spacing={1.5}
                                    >
                                        <BrandAvatar logo={brand.logo} size={40} />
                                        <Stack spacing={0.25} flex={1} minWidth={0}>
                                            <Typography
                                                fontFamily={FONTS.display}
                                                fontWeight={600}
                                                fontSize="14px"
                                                noWrap
                                            >
                                                {brand.name}
                                            </Typography>
                                            <Typography
                                                fontSize="12px"
                                                color="text.secondary"
                                                noWrap
                                            >
                                                {brand.status} • Expires{" "}
                                                {brand.expires}
                                            </Typography>
                                        </Stack>
                                    </Stack>
                                ))}
                            </Stack>
                        </>
                    )}
                </Stack>
            </Stack>
        </>
    );
};

export default PartnerDashboard;
